import React from 'react';

import { STATUS_COLORS } from '../../theme/tokens';
import { Icon } from '../Icon/Icon';
import { Root } from './CopyButton.styles';
import { useClipboard } from './useClipboard';

export type CopyButtonProps = {
  /** The exact text written to the clipboard. */
  value: string;
  /** Accessible name, already translated (`label={t('Copy host')}`). */
  label: string;
  /** Glyph size in px. */
  size?: number;
  /** Fired with the value once it actually reached the clipboard. */
  onCopied?: (value: string) => void;
  /** Fired when neither clipboard path could write the value. */
  onCopyFailed?: () => void;
  className?: string;
};

/**
 * Bare icon button that copies `value` and flips its glyph to a positive check
 * for a beat (the toast hold, TOAST_ANIM.HOLD_MS) so the user sees the copy
 * landed. Clipboard access, with the execCommand fallback, lives in the
 * co-located `useClipboard` hook, exported from this barrel for consumers that
 * need the behaviour without the button. `data-copied` mirrors the state for
 * tests and consumer styling.
 */
export const CopyButton = ({
  value,
  label,
  size = 14,
  onCopied,
  onCopyFailed,
  className,
}: CopyButtonProps): React.ReactElement => {
  const { copied, copy } = useClipboard();

  const onClick = React.useCallback(async (): Promise<void> => {
    const ok = await copy(value);
    if (ok) onCopied?.(value);
    else onCopyFailed?.();
  }, [copy, value, onCopied, onCopyFailed]);

  return (
    <Root
      type="button"
      className={className}
      aria-label={label}
      data-copied={copied ? 'true' : 'false'}
      onClick={() => void onClick()}
    >
      {copied ? <Icon name="check" size={size} color={STATUS_COLORS.positive} /> : <Icon name="copy" size={size} />}
    </Root>
  );
};

export default CopyButton;
